import type { RootBlockModel } from '@blocksuite/affine/model';
import { Service } from '@toeverything/infra';
import type { Array as YArray, Map as YMap } from 'yjs';

import type { DocCreateMiddleware, DocRecord } from '../../doc';
import { DEFAULT_DOC_MODE } from '../../doc/constants';
import type { DocsStore } from '../../doc/stores/docs';
import type { DocCreateOptions } from '../../doc/types';
import type { AppThemeService } from '../../theme';
import type { WorkspaceService } from '../../workspace';
import type { EdgelessDefaultTheme } from '../schema';
import type { EditorSettingService } from '../services/editor-setting';

const AUTO_TITLE_BASE = 'New Doc';

const getValueByDefaultTheme = (
  defaultTheme: EdgelessDefaultTheme,
  currentAppTheme: string
) => {
  switch (defaultTheme) {
    case 'dark':
      return 'dark';
    case 'light':
      return 'light';
    case 'blue-white':
      return 'blue-white';
    case 'specified':
      // Map app theme to edgeless theme
      if (currentAppTheme === 'dark') return 'dark';
      if (currentAppTheme === 'blue-white') return 'blue-white';
      return 'light';
    case 'auto':
      return 'system';
    default:
      return 'system';
  }
};

export class EditorSettingDocCreateMiddleware
  extends Service
  implements DocCreateMiddleware
{
  constructor(
    private readonly editorSettingService: EditorSettingService,
    private readonly appThemeService: AppThemeService,
    private readonly workspaceService: WorkspaceService,
    private readonly docsStore: DocsStore
  ) {
    super();
  }

  beforeCreate(docCreateOptions: DocCreateOptions): DocCreateOptions {
    // clone the docCreateOptions to avoid mutating the original object
    docCreateOptions = {
      ...docCreateOptions,
    };

    const preferMode =
      this.editorSettingService.editorSetting.settings$.value.newDocDefaultMode;
    const mode = preferMode === 'ask' ? DEFAULT_DOC_MODE : preferMode;
    docCreateOptions.primaryMode ??= mode;

    docCreateOptions.docProps = {
      ...docCreateOptions.docProps,
      note: this.editorSettingService.editorSetting.get('affine:note'),
    };

    return docCreateOptions;
  }

  /**
   * E029: Get all existing doc titles from Y.js synchronously
   */
  private getExistingTitles(): Set<string> {
    const titles = new Set<string>();
    try {
      const meta = this.workspaceService.workspace.rootYDoc.getMap('meta');
      const pages = meta.get('pages') as YArray<YMap<unknown>> | undefined;
      if (pages) {
        for (const page of pages) {
          const title = page.get('title');
          if (typeof title === 'string' && title) {
            titles.add(title);
          }
        }
      }
    } catch (e) {
      console.warn('[AutoTitle] Failed to get existing titles:', e);
    }
    return titles;
  }

  /**
   * E029: Generate unique title like "New Doc", "New Doc (2)", etc.
   */
  private generateUniqueTitle(existingTitles: Set<string>): string {
    if (!existingTitles.has(AUTO_TITLE_BASE)) {
      return AUTO_TITLE_BASE;
    }
    let counter = 2;
    while (counter < 1000) {
      const candidate = `${AUTO_TITLE_BASE} (${counter})`;
      if (!existingTitles.has(candidate)) {
        return candidate;
      }
      counter++;
    }
    return `${AUTO_TITLE_BASE} (${Date.now()})`;
  }

  afterCreate(doc: DocRecord, docCreateOptions: DocCreateOptions) {
    const edgelessDefaultTheme = getValueByDefaultTheme(
      this.editorSettingService.editorSetting.get('edgelessDefaultTheme'),
      this.appThemeService.appTheme.theme$.value ?? 'light'
    );
    doc.setProperty('edgelessColorTheme', edgelessDefaultTheme);

    // E029: Auto-assign unique title as FIRST ACTION after creation
    // This triggers a Y.js update which forces sync to backend
    if (!docCreateOptions.title) {
      const existingTitles = this.getExistingTitles();
      const newTitle = this.generateUniqueTitle(existingTitles);
      console.log(
        `[AutoTitle] Setting title "${newTitle}" for doc ${doc.id} as first action`
      );

      // Change title in the Y.js block (not just meta)
      const blockSuiteDoc = this.docsStore.getBlockSuiteDoc(doc.id);
      if (blockSuiteDoc) {
        const pageBlock = blockSuiteDoc.getBlocksByFlavour('affine:page').at(0)
          ?.model as RootBlockModel | undefined;
        if (pageBlock) {
          blockSuiteDoc.transact(() => {
            pageBlock.props.title.delete(0, pageBlock.props.title.length);
            pageBlock.props.title.insert(newTitle, 0);
          });
          console.log(`[AutoTitle] Y.js block title updated for doc ${doc.id}`);
        }
      }
      // Also update meta
      doc.setMeta({ title: newTitle });
    }
  }
}
